import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, forwardRef, ChangeDetectorRef, HostListener, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CompanyManagementService } from 'src/app/features/admin-portal/modules/company-management/Service/company-management.service';
import { IconComponent } from '../icon/icon.component';

export interface CompanySearchResult {
  id: number;
  name: string;
  email?: string;
  address?: string;
  code?: string;
}

@Component({
  selector: 'app-company-search',
  templateUrl: './company-search.component.html',
  styleUrls: ['./company-search.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, IconComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CompanySearchComponent),
      multi: true
    }
  ]
})
export class CompanySearchComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() placeholder = 'Search company...';
  @Input() minSearchLength = 3;
  @Input() disabled = false;
  @Input() set initialValue(value: CompanySearchResult | null) {
    if (value) {
      this.writeValue(value);
    }
  }
  @Output() companySelected = new EventEmitter<CompanySearchResult>();
  @Output() companyCleared = new EventEmitter<void>();

  searchText = '';
  filteredCompanies: CompanySearchResult[] = [];
  isSearching = false;
  showDropdown = false;
  selectedCompany: CompanySearchResult | null = null;

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  private onChange: any = () => {};
  private onTouched: any = () => {};

  constructor(
    private companyService: CompanyManagementService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
  ) {}

  /** Close dropdown when the user clicks anywhere outside this component */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.showDropdown = false;
    }
  }

  ngOnInit(): void {
    this.setupSearch();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearch(): void {
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(searchText => {
      this.searchCompanies(searchText);
    });
  }

  onSearchInput(searchText: string): void {
    this.searchText = searchText;

    if (!this.selectedCompany) {
      this.onChange(null);
    }

    if (searchText.length >= this.minSearchLength) {
      this.showDropdown = true;
      this.searchSubject.next(searchText);
    } else {
      this.showDropdown = false;
      this.filteredCompanies = [];
    }
  }

  onFocus(): void {
    this.onTouched();
    if (this.searchText.length >= this.minSearchLength) {
      this.showDropdown = true;
      let query = this.searchText;
      if (this.selectedCompany && this.searchText === this.selectedCompany.name) {
        query = '';
      }
      this.searchSubject.next(query);
    }
  }

  searchCompanies(searchText: string): void {
    this.isSearching = true;
    this.companyService.getCompaniesLite(searchText)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response: any) => {
          this.showDropdown = true;
          if (Array.isArray(response)) {
            this.filteredCompanies = response;
          } else if (response && response.items) {
            this.filteredCompanies = response.items;
          } else {
            this.filteredCompanies = [];
          }
          this.isSearching = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error searching companies:', error);
          this.filteredCompanies = [];
          this.showDropdown = true;
          this.isSearching = false;
          this.cdr.markForCheck();
        }
      });
  }

  selectCompany(company: CompanySearchResult): void {
    this.selectedCompany = company;
    this.searchText = company.name;
    this.showDropdown = false;
    this.onChange(company);
    this.companySelected.emit(company);
  }

  clearSelection(): void {
    this.selectedCompany = null;
    this.searchText = '';
    this.filteredCompanies = [];
    this.showDropdown = false;
    this.onChange(null);
    this.companyCleared.emit();
  }

  // ControlValueAccessor implementation
  writeValue(value: CompanySearchResult | null): void {
    if (value) {
      this.selectedCompany = value;
      this.searchText = value.name;
    } else {
      this.selectedCompany = null;
      this.searchText = '';
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
